import cn from "classnames";
import { styled } from "@linaria/react";
import React from "react";
import { color, easingBounce, easingBounceMs } from "..";
import ValidityDot from "../ValidityDot";
import { css } from "@linaria/core";

const RoundedButton = styled.button`
  all: unset;
  cursor: pointer;
  color: ${color.carbon800};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  padding: 6px;
  aspect-ratio: 1;
  position: relative;
  isolation: isolate;

  &.dark {
    color: ${color.hydrogen};
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: ${color.titanium100};
    transition-property: transform, opacity, background-color;
    transition-duration: ${easingBounceMs}ms;
    transition-timing-function: ${easingBounce};
    transform: scale(0);
    border-radius: inherit;
    z-index: -1;
  }

  :not([disabled]):not(.minified):hover::before {
    transform: scale(1);
    background-color: ${color.titanium100};
  }

  :is(:not([disabled]):not(.minified):active:hover)::before {
    transform: scale(1);
    background-color: ${color.titanium200};
  }

  &.dark {
    color: ${color.hydrogen};

    &:not([disabled]):not(.minified):hover::before {
      opacity: 0.3;
    }

    &:is(:active:hover):not(.minified)::before {
      opacity: 0.7;
    }
  }

  &[disabled] {
    opacity: 0.5;
    /* color: ${color.carbon300}; */
    cursor: not-allowed;
  }

  &.checked:not([disabled]) {
    color: ${color.lightNitrogen500_Main};

    &::before {
      transform: scale(1);
      /* opacity: 0.3; */
      background-color: ${color.lightNitrogen50};
    }
    :is(:hover)::before {
      opacity: 1;
      background-color: ${color.lightNitrogen100};
    }
  }
`;

interface P extends React.HTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  children: React.ReactNode;
  checked?: boolean;
  type?: "button" | "submit" | "reset";
  theme?: "light" | "dark";
}

const ButtonIconV2 = (
  {
    children,
    disabled,
    theme = "light",
    className,
    onClick,
    checked,
    type = "button",
    ...props
  }: P,
  forwardedRef: React.Ref<HTMLButtonElement>,
) => (
  <RoundedButton
    ref={forwardedRef}
    onClick={disabled ? undefined : onClick}
    className={cn(className, theme, { checked })}
    type={type}
    disabled={disabled}
    {...props}
  >
    {children}
  </RoundedButton>
);

export default React.forwardRef(ButtonIconV2);

/** @description Only works within ButtonIcon */
export function ButtonIconMarker() {
  return <ValidityDot state="info" className={buttonIconHint} />;
}

const buttonIconHint = css`
  @keyframes spawn {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }
  position: absolute;
  top: 1px;
  right: 1px;
  animation: spawn 150ms cubic-bezier(0.64, 0.57, 0.67, 1.53) forwards;
`;
